import { Link } from 'react-router-dom';
import PortalLayout from '@/components/layouts/PortalLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  CheckCircle2, 
  Users, 
  Clock, 
  HardHat, 
  PartyPopper,
  Building2,
  Accessibility,
  Droplets,
  ArrowRight,
  Calculator,
  AlertCircle
} from 'lucide-react';

const unitRecommendations = [
  { guests: '1-50', units: '1-2', ada: '1 recommended', handwash: 'Optional' },
  { guests: '51-100', units: '2-4', ada: '1 required', handwash: '1 recommended' },
  { guests: '101-250', units: '4-6', ada: '1-2 required', handwash: '1-2 recommended' },
  { guests: '251-500', units: '6-10', ada: '2 required', handwash: '2-3 recommended' },
  { guests: '500+', units: '10+', ada: '2+ required', handwash: '3+ recommended' },
];

const constructionRecommendations = [
  { workers: '1-10', units: '1', service: 'Weekly' },
  { workers: '11-20', units: '2', service: 'Twice weekly' },
  { workers: '21-40', units: '3-4', service: 'Twice weekly' },
  { workers: '41-60', units: '5-6', service: '3x weekly' },
  { workers: '60+', units: '1 per 10 workers', service: 'Daily' },
];

export default function PlanningGuide() {
  return (
    <PortalLayout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#194461] to-[#091D2E] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="page-title">
            Portable Toilet Planning Guide
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Not sure how many units you need? We'll help you figure out the right combination of standard units, ADA-compliant units, and handwash stations for your event or job site.
          </p>
        </div>
      </section>

      {/* Quick Calculator CTA */}
      <section className="py-8 bg-[#52B8E1]/10 border-b border-[#52B8E1]/20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calculator className="text-[#194461]" size={24} />
              <span className="font-medium text-slate-900">Ready to get a quote?</span>
            </div>
            <Link to="/portal/request-quote">
              <Button className="bg-[#194461] hover:bg-[#091D2E]">
                Request a Quote <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Unit Types Explained */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Understanding Unit Types</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Standard Units */}
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#52B8E1]/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="text-[#52B8E1]" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Standard Units</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Our most common portable toilet. Perfect for general use at events and construction sites.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Fits most adults comfortably</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Includes hand sanitizer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Lockable door</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* ADA Units */}
            <Card className="border-[#52B8E1] border-2">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#194461] rounded-lg flex items-center justify-center mb-4">
                  <Accessibility className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">ADA-Compliant Units</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Wheelchair-accessible units that meet Americans with Disabilities Act requirements.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Extra-wide door (min. 32")</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Interior grab bars</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Spacious interior for wheelchairs</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-800 font-medium flex items-start gap-2">
                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                    Required by law for public events with 50+ attendees
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Handwash Stations */}
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#52B8E1]/10 rounded-lg flex items-center justify-center mb-4">
                  <Droplets className="text-[#52B8E1]" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Handwash Stations</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Standalone sinks with fresh water, soap, and paper towels for proper hand hygiene.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Fresh water tank</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Soap dispenser included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Paper towel holder</span>
                  </li>
                </ul>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-800 font-medium flex items-start gap-2">
                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                    Required for food service events & many construction sites
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Events Guide */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#194461] rounded-lg flex items-center justify-center">
              <PartyPopper className="text-white" size={20} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Special Events & Festivals</h2>
          </div>

          <Card className="border-slate-200 mb-8">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 border-b">Expected Guests</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 border-b">Standard Units</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 border-b">ADA Units</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 border-b">Handwash Stations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unitRecommendations.map((row, idx) => (
                      <tr key={idx} className="border-b border-slate-100 last:border-0">
                        <td className="py-3 px-4 font-medium text-slate-900">{row.guests}</td>
                        <td className="py-3 px-4 text-slate-600">{row.units}</td>
                        <td className="py-3 px-4 text-slate-600">{row.ada}</td>
                        <td className="py-3 px-4 text-slate-600">{row.handwash}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-2">Add more units if:</h4>
              <ul className="space-y-1 text-sm text-slate-600">
                <li>• Event serves alcohol (add 20-30%)</li>
                <li>• Event is longer than 4 hours</li>
                <li>• Limited other restroom access</li>
                <li>• High female attendance (longer use times)</li>
              </ul>
            </div>
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-2">When ADA is required:</h4>
              <ul className="space-y-1 text-sm text-slate-600">
                <li>• Any public event with 50+ guests</li>
                <li>• Events on public property</li>
                <li>• Events receiving public funding</li>
                <li>• When no other accessible restrooms are nearby</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Construction Guide */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#194461] rounded-lg flex items-center justify-center">
              <HardHat className="text-white" size={20} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Construction Sites (OSHA Guidelines)</h2>
          </div>

          <Card className="border-slate-200 mb-8">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 border-b">Workers on Site</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 border-b">Minimum Units</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 border-b">Recommended Service</th>
                    </tr>
                  </thead>
                  <tbody>
                    {constructionRecommendations.map((row, idx) => (
                      <tr key={idx} className="border-b border-slate-100 last:border-0">
                        <td className="py-3 px-4 font-medium text-slate-900">{row.workers}</td>
                        <td className="py-3 px-4 text-slate-600">{row.units}</td>
                        <td className="py-3 px-4 text-slate-600">{row.service}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <AlertCircle size={18} />
              OSHA Requirements
            </h4>
            <ul className="space-y-1 text-sm text-amber-800">
              <li>• OSHA requires 1 toilet per 20 workers on construction sites</li>
              <li>• Handwash facilities must be provided when workers handle food or hazardous materials</li>
              <li>• Units must be serviced regularly to maintain sanitary conditions</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Long-term Rentals */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#194461] rounded-lg flex items-center justify-center">
              <Building2 className="text-white" size={20} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Long-term & Commercial Rentals</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3">Ideal for:</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5" />
                    <span>Multi-month construction projects</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5" />
                    <span>Facility renovations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5" />
                    <span>Remote work sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5" />
                    <span>Agricultural operations</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3">Service options:</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <Clock size={16} className="text-[#52B8E1] mt-0.5" />
                    <span><strong>Weekly:</strong> Standard for most sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock size={16} className="text-[#52B8E1] mt-0.5" />
                    <span><strong>Twice weekly:</strong> Higher traffic sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock size={16} className="text-[#52B8E1] mt-0.5" />
                    <span><strong>Daily:</strong> Large crews or events</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock size={16} className="text-[#52B8E1] mt-0.5" />
                    <span><strong>On-call:</strong> As-needed service</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#194461] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold mb-4">Still Not Sure? We're Here to Help!</h2>
          <p className="text-slate-300 mb-8">
            Our team has years of experience helping customers find the right solution. Tell us about your event or project and we'll recommend the perfect setup.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/portal/request-quote">
              <Button size="lg" className="bg-[#52B8E1] hover:bg-[#3AA0C9] text-[#091D2E] font-semibold px-8">
                Request a Quote
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
            <a href="tel:4235550100">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                Call (423) 555-0100
              </Button>
            </a>
          </div>
        </div>
      </section>
    </PortalLayout>
  );
}
